import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { ApiError } from "@/lib/api";
import { chosenCampaign } from "@/lib/manual-call";
import { isDialable } from "@/lib/phone-number";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { canDialSecondLine, dialBlockedReason } from "@/softphone/call-control";
import { CampaignPicker, useOutboundDial } from "@/softphone/manual-call";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * A number printed on a record, which calling it back is one click away from.
 *
 * The first surface is the rep's own call history — the number they were just talking to is the
 * number they most often want next, and until now reading it off the table and typing it back
 * into the dialler was the whole workflow. It renders **only the number**, so whatever is around
 * it keeps owning the name, the layout and the column.
 *
 * ## Three decisions worth stating
 *
 * **A confirmation, not a dial.** Every other way this console places a call starts with a
 * deliberate act — typing a number and pressing Call, or opening a picker and choosing a person.
 * A number in a table is *content*: the rep is reading it, and a click that went straight to the
 * switch would turn a mis-click, a stray tap on a phone, or a click meant for the row into a real
 * telephone ringing in somebody's house. So the click states the intention and the dialog carries
 * it out. That is also what makes this safe to nest inside a row that is itself a button, where
 * `features/call-report.tsx` deliberately refused to put a play button: a mis-click here costs
 * one Escape.
 *
 * **It does not decide what a number means.** `isDialable` only rejects the things in a CDR's
 * number column that are not numbers at all — `unknown` on a withheld caller id, a room name, the
 * switch's `s` placeholder — and everything else is handed to `phone.dial`, which is the one
 * place a destination becomes a dial string (`lib/phone-number.ts`). No `+1` is added here; the
 * dialplan does that, and a second copy of that rule is a second thing to change.
 *
 * **Being unable to dial is a reason, not an absence.** A phone that is not registered, is
 * registered in another tab, or has a leg still ringing cannot take this — and in all three cases
 * the number still opens the dialog, which says which one it is and dims Call. Dimming the
 * numbers in the table instead would grey out a column of content over a state of the phone, and
 * hiding the button would leave a rep clicking dead text. The sentences come from
 * `dialBlockedReason`, shared with the phone page's Dial now card so the two cannot word the same
 * phone differently.
 */
export function DialNumberButton({
  number, name, className,
}: {
  /** The number as the record holds it — punctuated, or not a number at all. */
  number: string | null;
  /** Who it belongs to, when the record knows. Used in the confirmation, not on the button. */
  name?: string | null;
  className?: string;
}) {
  const phone = useSoftphone();
  const out = useOutboundDial();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [refusal, setRefusal] = React.useState<string | null>(null);

  const shown = number ? formatPhoneNumber(number) : "—";

  // `available` is "an endpoint is assigned to this account". Without one there is no dock to
  // dial from and no reason to explain, so the number is just text — which is what every screen
  // that renders this shows such an account anyway.
  if (!number || !phone.available || !isDialable(number)) {
    return <span className={className}>{shown}</span>;
  }

  const blocked = dialBlockedReason(phone.registration, canDialSecondLine(phone.calls));
  // Through the rep's campaign when this is an outside number and they are between calls on one
  // (ADR-047, ADR-068): the dialer places it and the ACD brings the answer back to this phone. The
  // confirmation says so, so the rep is not surprised when their own phone rings.
  const path = out.path(number);
  const campaigns = path.via === "campaign" ? path.campaigns : [];
  const [picked, setPicked] = React.useState<string | null>(null);
  const through =
    (picked && campaigns.find((c) => c.campaign_id === picked)) ||
    chosenCampaign(campaigns, out.remembered);
  const viaCampaign = path.via === "campaign";

  const call = async () => {
    setBusy(true);
    setFailed(false);
    setRefusal(null);
    try {
      await out.place(number, through?.campaign_id ?? null);
      // Nothing announces success here: the dock opens itself on a call it is placing, shows who
      // it is ringing and plays the ringback. A toast saying the same thing would be a second
      // answer to a question already answered, half a second later and somewhere else.
      setOpen(false);
    } catch (e) {
      // A refusal from core-api has its own sentence, shown here. `phone.dial` puts every
      // refusal of its own into the phone's banner, which is in the dock behind this dialog — so
      // the flag goes up here and the sentence is read back off `phone.error` at render, where
      // it is current. The dialog stays open either way: retrying is one click.
      if (e instanceof ApiError) setRefusal(e.message);
      else setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    // Everything this component renders is contained here, and the wrapper is load-bearing.
    //
    // A row in a call table is itself `role="button"` — it opens the call's detail drawer — so
    // this button sits inside a button. Two things then have to be stopped, and stopping them at
    // one boundary is the only version that stays correct:
    //
    //  - **The click**, or the drawer opens behind the confirmation.
    //  - **Enter and Space**, because the row's key handler calls `preventDefault()` — which
    //    stops the browser ever turning the keypress into a click on this button at all.
    //
    // And it must be the wrapper rather than the button, because the confirmation is a React
    // child of this component wherever the portal puts its DOM: React bubbles synthetic events
    // through the *React* tree, so a click on Cancel inside the dialog reaches the row's
    // `onClick` exactly as a click on the number does. Guarding only the button leaves the drawer
    // opening behind a dialog the rep just dismissed.
    <span
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      }}
    >
      <button
        type="button"
        onClick={() => {
          setFailed(false);
          setOpen(true);
        }}
        aria-label={`Call ${shown}`}
        className={cn(
          "rounded underline decoration-border-strong decoration-dotted underline-offset-4",
          "transition-colors hover:text-accent-11 hover:decoration-accent-11",
          className,
        )}
      >
        {shown}
      </button>

      <ConfirmDialog
        open={open}
        onOpenChange={(next) => !busy && setOpen(next)}
        title={`Call ${shown}?`}
        description={
          <>
            {name ? `${name}. ` : ""}
            {viaCampaign
              ? through
                ? `Goes out through ${through.campaign_name ?? through.name}. When they answer, your phone rings and you are connected.`
                : "Choose the campaign this call goes out through."
              : "Your phone places the call. Anything already on the line goes on hold."}
            {campaigns.length > 1 && (
              <span className="mt-3 block">
                <CampaignPicker
                  campaigns={campaigns}
                  value={through?.campaign_id ?? null}
                  onChange={(value) => {
                    setPicked(value);
                    out.remember(value);
                  }}
                  disabled={busy}
                />
              </span>
            )}
            {blocked && <span className="mt-2 block text-warning-11">{blocked}</span>}
            {refusal && <span className="mt-2 block text-danger-11">{refusal}</span>}
            {failed && (
              <span className="mt-2 block text-danger-11">
                {phone.error ?? "The call did not go out."}
              </span>
            )}
          </>
        }
        confirmLabel="Call"
        confirmDisabled={blocked !== null || (viaCampaign && through === null)}
        loading={busy}
        onConfirm={() => void call()}
      />
    </span>
  );
}
